import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle, XCircle, Loader2, ArrowRight } from "lucide-react";

import { API_URL } from "@/utils/api";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("No verification token provided");
      return;
    }

    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token })
      });

      // Clone response before reading it
      const responseClone = response.clone();
      
      let data;
      try {
        data = await response.json();
      } catch (e) {
        // If JSON parsing fails, try reading text
        const text = await responseClone.text();
        throw new Error(text || "Verification failed");
      }

      if (!response.ok) {
        throw new Error(data.detail || "Verification failed");
      }

      // Store the token and redirect
      localStorage.setItem("auth_token", data.access_token);
      setStatus("success");
      toast.success("Email verified successfully!");
      
      // Redirect after a short delay - use window.location for Safari iOS
      setTimeout(() => {
        window.location.replace("/dashboard");
      }, 2000);
    } catch (err) {
      setStatus("error");
      setErrorMessage(err.message);
      toast.error(err.message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center font-['Inter'] bg-white">
      <div className="max-w-md w-full mx-auto px-8 text-center">
        {status === "verifying" && (
          <>
            <div className="w-24 h-24 bg-[#F7FFE5] rounded-full flex items-center justify-center mx-auto mb-8 border-2 border-dashed border-[#BFFF00]">
              <Loader2 className="w-12 h-12 text-[#121212] animate-spin" />
            </div>
            <h1 className="text-3xl font-bold text-[#121212] mb-4 font-['Manrope']">Verifying your email...</h1>
            <p className="text-[#121212]/60">Please wait while we verify your email address.</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-24 h-24 bg-[#BFFF00] rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-[#BFFF00]/30">
              <CheckCircle className="w-14 h-14 text-[#121212]" />
            </div>
            <h1 className="text-3xl font-bold text-[#121212] mb-4 font-['Manrope']">Email verified!</h1>
            <p className="text-[#121212]/60 mb-8">Your account is now active. Redirecting to dashboard...</p>
            
            <Button
              onClick={() => window.location.replace("/dashboard")}
              className="rounded-full bg-[#121212] hover:bg-[#2a2a2a] px-8"
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-8">
              <XCircle className="w-14 h-14 text-red-500" />
            </div>
            <h1 className="text-3xl font-bold text-[#121212] mb-4 font-['Manrope']">Verification failed</h1>
            <p className="text-red-500 mb-8">{errorMessage}</p>
            
            <div className="space-y-4">
              <Link to="/register">
                <Button className="w-full rounded-full bg-[#121212] hover:bg-[#2a2a2a]">
                  Register again
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="w-full rounded-full border-2 border-[#121212]">
                  Back to login
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
